import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Search, Filter, Loader2, Paperclip, X, FileText, Building2, Archive, ChevronRight, RefreshCw, AlertCircle } from "lucide-react";

const RESOURCE_LABELS = {
  news: "News",
  report: "Report standard",
  briefing: "Report Mercati Energetici",
  custom_report: "Report Personalizzato",
};

export default function AdminEmailLog() {
  const [logs, setLogs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterResource, setFilterResource] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [includeAudit, setIncludeAudit] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCompany) params.append("company_id", filterCompany);
      if (filterResource) params.append("resource_type", filterResource);
      if (filterStatus) params.append("status", filterStatus);
      if (filterFrom) params.append("date_from", filterFrom);
      if (filterTo) params.append("date_to", filterTo);
      params.append("include_audit", includeAudit ? "true" : "false");
      params.append("limit", "300");
      const [{ data: rows }, { data: comps }] = await Promise.all([
        api.get(`/email-logs?${params.toString()}`),
        api.get("/companies"),
      ]);
      setLogs(Array.isArray(rows) ? rows : []);
      setCompanies(Array.isArray(comps) ? comps : []);
    } catch (e) {
      toast.error("Errore caricamento log email");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCompany, filterResource, filterStatus, filterFrom, filterTo, includeAudit]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((l) =>
      [l.recipient_email, l.subject, l.body_text, l.recipient_company_name]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [logs, search]);

  const clearFilters = () => {
    setFilterCompany("");
    setFilterResource("");
    setFilterStatus("");
    setFilterFrom("");
    setFilterTo("");
    setIncludeAudit(true);
    setSearch("");
  };

  const anyFilter = filterCompany || filterResource || filterStatus || filterFrom || filterTo || !includeAudit || search;

  const [retrying, setRetrying] = useState(false);
  const retryFailed = async () => {
    if (retrying) return;
    if (!window.confirm("Rispedire tutte le email fallite del range selezionato? (max 50 per chiamata, rate-limitato Resend)")) return;
    setRetrying(true);
    try {
      const params = new URLSearchParams();
      if (filterFrom) params.append("date_from", filterFrom);
      if (filterTo) params.append("date_to", filterTo);
      params.append("max_items", "50");
      const { data } = await api.post(`/email-logs/retry-failed?${params.toString()}`);
      toast.success(`Retry: ${data.succeeded} ok · ${data.failed} ancora falliti su ${data.retried}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore retry");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Storico Email Inviate</h1>
        <p className="text-sm text-slate-500 mt-1">
          Traccia ogni email transazionale inviata dal portale: destinatario, oggetto, testo, allegati, mittente, timestamp.
        </p>
      </div>

      <div className="card-elevated overflow-hidden">
        {/* Filters */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap bg-slate-50/50">
          <Filter size={14} className="text-slate-400" />
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
            data-testid="el-filter-company"
          >
            <option value="">Tutte le aziende</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={filterResource}
            onChange={(e) => setFilterResource(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
            data-testid="el-filter-resource"
          >
            <option value="">Tutti i tipi</option>
            {Object.entries(RESOURCE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
            data-testid="el-filter-status"
          >
            <option value="">Tutti gli stati</option>
            <option value="sent">Inviate</option>
            <option value="sent_partial">Inviate parzialmente (allegati esclusi)</option>
            <option value="failed">Non inviate (errore)</option>
            <option value="skipped_opt_out">Saltate (email disattivate)</option>
          </select>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            Dal
            <input
              type="date"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              className="px-2 py-1 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
              data-testid="el-filter-from"
            />
            al
            <input
              type="date"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              className="px-2 py-1 text-sm rounded-lg border border-slate-200 bg-white outline-none focus:border-blue-500"
              data-testid="el-filter-to"
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={includeAudit}
              onChange={(e) => setIncludeAudit(e.target.checked)}
              data-testid="el-filter-audit"
            />
            Includi archivio [ARCHIVIO]
          </label>
          <div className="flex-1"></div>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca oggetto, testo, email…"
              className="pl-8 pr-3 py-1.5 text-sm rounded-lg border border-slate-200 outline-none focus:border-blue-500 w-64"
              data-testid="el-search"
            />
          </div>
          {anyFilter && (
            <button
              onClick={clearFilters}
              className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg bg-white border border-slate-200"
              data-testid="el-clear-filters"
            >
              Azzera
            </button>
          )}
          {filterStatus === "failed" && (
            <button
              onClick={retryFailed}
              disabled={retrying}
              className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="el-retry-failed"
            >
              {retrying ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Rispedisci fallite
            </button>
          )}
        </div>

        {/* List */}
        <div className="px-5 py-2 border-b border-slate-100 text-xs text-slate-500 flex items-center gap-2">
          <Mail size={13} className="text-blue-600" />
          <span>{filtered.length} email {filtered.length === 1 ? "trovata" : "trovate"}</span>
        </div>
        {loading ? (
          <div className="p-10 flex items-center justify-center text-slate-500 gap-2 text-sm">
            <Loader2 className="animate-spin" size={14} /> Caricamento…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            Nessuna email trovata con questi filtri.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
            {filtered.map((l) => (
              <button
                key={l.id}
                onClick={() => setSelected(l)}
                className="w-full text-left px-5 py-3 hover:bg-slate-50 flex items-start gap-3"
                data-testid={`el-row-${l.id}`}
              >
                {l.status === "skipped_opt_out" ? (
                  <span className="mt-0.5" title="Il destinatario ha disattivato le email nel proprio Profilo">📭</span>
                ) : l.is_audit_copy ? (
                  <Archive size={16} className="text-amber-500 mt-0.5" title="Archivio" />
                ) : (
                  <Mail size={16} className="text-blue-500 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 truncate">{l.subject || "—"}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase tracking-wide">
                      {RESOURCE_LABELS[l.related_resource_type] || l.related_resource_type || l.kind}
                    </span>
                    {l.attachments_count > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 flex items-center gap-1">
                        <Paperclip size={10} /> {l.attachments_count}
                      </span>
                    )}
                    {l.status === "failed" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700">Non inviata</span>
                    )}
                    {l.status === "sent_partial" && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800"
                        title={`Alcuni allegati sono stati esclusi per superamento del limite 20MB: ${(l.dropped_attachments || []).join(", ")}`}
                      >
                        Inviata (allegati parziali)
                      </span>
                    )}
                    {l.status === "skipped_opt_out" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700" title="Il cliente ha disattivato la ricezione email">
                        Email disattivate dal cliente
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-700">→ {l.recipient_email}</span>
                    {l.recipient_company_name && (
                      <span className="flex items-center gap-1"><Building2 size={11} /> {l.recipient_company_name}</span>
                    )}
                    <span>· {new Date(l.sent_at).toLocaleString("it-IT")}</span>
                  </div>
                  {l.body_text && (
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2">{l.body_text}</div>
                  )}
                </div>
                <ChevronRight size={14} className="text-slate-300 mt-1" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="el-detail-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4 gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <FileText size={12} /> {RESOURCE_LABELS[selected.related_resource_type] || selected.related_resource_type || selected.kind}
                  {selected.is_audit_copy && <span className="text-amber-600">· ARCHIVIO</span>}
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-900 mt-1 break-words">
                  {selected.subject || "—"}
                </h3>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Destinatario</div>
                <div className="text-slate-800">{selected.recipient_email}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Azienda cliente</div>
                <div className="text-slate-800">{selected.recipient_company_name || "—"}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Mittente</div>
                <div className="text-slate-800 text-xs">{selected.sender}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Data invio</div>
                <div className="text-slate-800">{new Date(selected.sent_at).toLocaleString("it-IT")}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Allegati</div>
                <div className="text-slate-800">{selected.attachments_count || 0}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Stato</div>
                <div className={
                  selected.status === "sent" ? "text-emerald-700"
                  : selected.status === "sent_partial" ? "text-amber-700"
                  : selected.status === "skipped_opt_out" ? "text-red-700"
                  : "text-red-700"
                }>
                  {selected.status === "sent" ? "Inviata"
                    : selected.status === "sent_partial" ? `Inviata (${(selected.dropped_attachments || []).length} allegat${(selected.dropped_attachments || []).length === 1 ? "o" : "i"} esclus${(selected.dropped_attachments || []).length === 1 ? "o" : "i"} per limite 20MB)`
                    : selected.status === "skipped_opt_out" ? "Non inviata — email disattivate dal cliente"
                    : "Non inviata"}
                </div>
              </div>
              {selected.resend_id && (
                <div className="col-span-2">
                  <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Resend ID</div>
                  <div className="text-slate-800 font-mono text-xs">{selected.resend_id}</div>
                </div>
              )}
              {selected.error_message && (
                <div className="col-span-2">
                  <div className="text-[11px] text-red-500 uppercase tracking-wide font-semibold">Motivo errore</div>
                  <div className="text-red-700 font-mono text-xs bg-red-50 border border-red-100 rounded-md p-2 whitespace-pre-wrap break-words">
                    {selected.error_message}
                  </div>
                </div>
              )}
              {selected.last_retry_error && (
                <div className="col-span-2">
                  <div className="text-[11px] text-red-500 uppercase tracking-wide font-semibold">Ultimo retry — errore</div>
                  <div className="text-red-700 font-mono text-xs bg-red-50 border border-red-100 rounded-md p-2 whitespace-pre-wrap break-words">
                    {selected.last_retry_error}
                  </div>
                </div>
              )}
              {selected.retried_at && (
                <div className="col-span-2">
                  <div className="text-[11px] text-emerald-600 uppercase tracking-wide font-semibold">Retry eseguito il</div>
                  <div className="text-slate-800 text-xs">{new Date(selected.retried_at).toLocaleString("it-IT")}</div>
                </div>
              )}
            </div>
            {selected.body_text && (
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold mb-1">Testo email</div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap bg-slate-50 rounded-lg p-3 border border-slate-200">
                  {selected.body_text}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
